import { useCallback, useEffect, useRef, useState } from 'react';

import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { getSessionInfo } from '@northern.tech/store/auth';

const errors = {
  access_denied: 'You do not have permission to troubleshoot this device.',
  authentication_required: 'Your session expired. Sign in again.',
  terminal_busy: 'All terminal slots are occupied. Close a terminal and retry.',
  device_busy: 'Another tunnel operation is in progress. Try again shortly.',
  user_manager_unavailable: 'The terminal user’s systemd manager is unavailable. Rerun the updated Mender installer or install the latest z2-tunnel artifact.',
  user_lingering_required: 'Background tunnel setup is incomplete for the terminal user. Rerun the updated Mender installer or install the latest z2-tunnel artifact.',
  tunnel_credentials_unavailable: 'Tunnel credentials are unavailable on this device. Configure its environment profile, then try again.',
  tunnel_credentials_invalid: 'Tunnel credentials are invalid or unreadable by the device’s terminal user. Check its environment profile and permissions.',
  tunnel_configuration_invalid: 'The device’s tunnel configuration is invalid.',
  unsafe_tunnel_executable: 'The tunnel installation has unsafe ownership. Install the tunnel repair artifact.',
  device_command_failed: 'A required device command failed. Install the tunnel repair artifact or check the device logs.',
  device_command_timeout: 'A device command timed out. Reopen the dialog to verify its status.',
  tunnel_not_installed: 'The tunnel CLI is not installed. Rerun the updated Mender installer or install the latest z2-tunnel artifact.',
  tunnel_upgrade_required: 'Update the device’s tunnel CLI to a version supporting timed, IP-restricted tunnels.',
  visitor_ip_unavailable: 'Your public IP could not be verified. My IP access is unavailable.',
  unprivileged_terminal_user_required: 'Configure an unprivileged Mender terminal user before opening tunnels.',
  tunnel_limit: 'This device already has eight active tunnels.',
  terminal_timeout: 'The device did not respond in time. Reopen the dialog to check whether the operation completed.',
  terminal_unavailable: 'The device terminal is unavailable. Check the device connection and try again.',
  device_unavailable: 'The device is unavailable.',
  device_clock_or_expiry_invalid: 'Check the device clock. The requested tunnel has expired or its timing is invalid.'
};

class TunnelError extends Error {
  constructor(
    message: string,
    public retryNewRequest = false
  ) {
    super(message);
  }
}

export async function tunnelRequest(device: string, suffix = '', method = 'GET', body?: object, signal?: AbortSignal) {
  const { token } = getSessionInfo();
  if (!token) throw new Error(errors.authentication_required);
  const response = await fetch(`/api/management/v1/z2play/tunnels/devices/${encodeURIComponent(device)}${suffix}`, {
    method,
    signal,
    credentials: 'omit',
    cache: 'no-store',
    redirect: 'error',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {})
  });
  const data = await response.json();
  if (!response.ok)
    throw new TunnelError(
      response.status === 401 ? errors.authentication_required : errors[data.error] || 'Tunnel operation failed. Reopen the dialog to check its status.',
      data.retry_new_request === true
    );
  return data;
}

type Tunnel = {
  access: string;
  allow_cidr?: string;
  checked_at?: number;
  creator: string;
  endpoint?: string;
  expires_at: number;
  host: string;
  id: string;
  port: number;
  state: string;
  upstream_reachable?: boolean;
  verified?: boolean;
};

export default function PortForwardDialog({ device, open, onClose }: { device: string; onClose: () => void; open: boolean }) {
  const [host, setHost] = useState('127.0.0.1');
  const [port, setPort] = useState('');
  const [minutes, setMinutes] = useState(30);
  const [access, setAccess] = useState('my_ip');
  const [visitor, setVisitor] = useState<string>();
  const [items, setItems] = useState<Tunnel[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const requestId = useRef<string | undefined>(undefined);
  const requestDevice = useRef(device);
  const generation = useRef(0);
  const controllers = useRef(new Set<AbortController>());
  const terminal = (item: Tunnel) => ['stopped', 'expired', 'failed'].includes(item.state);
  const merge = useCallback((updates: Tunnel[]) => {
    setItems(previous => {
      const values = new Map(previous.map(item => [item.id, item]));
      updates.forEach(item => values.set(item.id, item));
      return [...values.values()].filter(item => !['stopped', 'expired', 'failed'].includes(item.state));
    });
  }, []);

  useEffect(() => {
    const current = ++generation.current;
    const activeControllers = controllers.current;
    const controller = new AbortController();
    controllers.current.add(controller);
    setItems([]);
    setError('');
    setNotice('');
    setVisitor(undefined);
    setBusy(false);
    if (requestDevice.current !== device) {
      requestId.current = undefined;
      requestDevice.current = device;
    }
    if (open) {
      setBusy(true);
      (async () => {
        const listed = await tunnelRequest(device, '', 'GET', undefined, controller.signal);
        if (current !== generation.current) return;
        merge(listed.tunnels);
        setVisitor(listed.visitor_ip);
        if (listed.tunnels.some((item: Tunnel) => !terminal(item))) {
          const refreshed = await tunnelRequest(device, '/refresh', 'POST', undefined, controller.signal);
          if (current === generation.current) merge(refreshed.tunnels);
        }
      })()
        .catch(failure => {
          if (current === generation.current && !controller.signal.aborted)
            setError(failure instanceof Error ? failure.message : 'Could not refresh tunnel status.');
        })
        .finally(() => {
          controllers.current.delete(controller);
          if (current === generation.current) setBusy(false);
        });
    }
    return () => {
      generation.current += 1;
      activeControllers.forEach(value => value.abort());
      activeControllers.clear();
    };
  }, [device, open, merge]);

  useEffect(() => {
    if (!open || !items.length) return;
    const expiry = Math.min(...items.map(item => item.expires_at * 1000));
    const timer = setTimeout(
      () => {
        setItems(previous => previous.filter(item => item.expires_at * 1000 > Date.now()));
      },
      Math.min(2147483647, Math.max(1, expiry - Date.now()))
    );
    return () => clearTimeout(timer);
  }, [items, open]);

  const close = () => {
    generation.current += 1;
    controllers.current.forEach(value => value.abort());
    controllers.current.clear();
    setError('');
    setNotice('');
    onClose();
  };
  const perform = async (action: (signal: AbortSignal) => Promise<{ notice?: string; tunnel?: Tunnel }>) => {
    const current = generation.current;
    const controller = new AbortController();
    controllers.current.add(controller);
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await action(controller.signal);
      if (current !== generation.current) return;
      if (result.tunnel) merge([result.tunnel]);
      if (result.notice) setNotice(result.notice);
    } catch (failure) {
      if (current !== generation.current || controller.signal.aborted) return;
      if (failure instanceof TunnelError && failure.retryNewRequest) requestId.current = undefined;
      setError(failure instanceof Error ? failure.message : 'Tunnel operation failed.');
    } finally {
      controllers.current.delete(controller);
      if (current === generation.current) setBusy(false);
    }
  };
  const changed = () => {
    requestId.current = undefined;
  };
  const create = () =>
    perform(async signal => {
      requestId.current ??= crypto.randomUUID().replaceAll('-', '');
      const result = await tunnelRequest(device, '', 'POST', { host, port: Number(port), minutes, access, request_id: requestId.current }, signal);
      if (!signal.aborted) requestId.current = undefined;
      return { ...result, notice: 'Tunnel requested. Reopen this dialog if its status needs verification.' };
    });
  const date = (seconds?: number) => (seconds ? new Date(seconds * 1000).toLocaleString() : 'Not verified');

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth aria-labelledby="port-forward-title">
      <DialogTitle id="port-forward-title">Port forwarding</DialogTitle>
      <DialogContent className="z2-tunnels-private">
        <Typography paragraph>
          Open a TCP connection to this device or a host on its network. Tunnels keep running when you close this dialog and stop at their expiry time.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {notice}
          </Alert>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Target host"
            value={host}
            onChange={event => {
              setHost(event.target.value);
              changed();
            }}
            helperText="Localhost or device-reachable LAN address"
            fullWidth
            disabled={busy}
          />
          <TextField
            label="TCP port"
            type="number"
            value={port}
            onChange={event => {
              setPort(event.target.value);
              changed();
            }}
            disabled={busy}
          />
          <TextField
            select
            label="Duration"
            value={minutes}
            onChange={event => {
              setMinutes(Number(event.target.value));
              changed();
            }}
            sx={{ minWidth: 150 }}
            disabled={busy}
          >
            {[15, 30, 60, 120].map(value => (
              <MenuItem key={value} value={value}>
                {value} minutes
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <TextField
          select
          label="Access"
          value={access}
          onChange={event => {
            setAccess(event.target.value);
            changed();
          }}
          fullWidth
          sx={{ mt: 2 }}
          disabled={busy}
        >
          <MenuItem value="my_ip">My IP{visitor ? ` (${visitor})` : ' — unavailable'}</MenuItem>
          <MenuItem value="public">Public</MenuItem>
        </TextField>
        <Typography variant="body2" sx={{ mt: 1 }}>
          TCP forwarding preserves the service’s protocol. It does not add HTTPS or a login. My IP remains fixed if your network address changes.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ my: 2 }}>
          <Button
            variant="contained"
            onClick={create}
            disabled={busy || !host || !Number.isInteger(Number(port)) || Number(port) < 1 || Number(port) > 65535 || (access === 'my_ip' && !visitor)}
          >
            Open tunnel
          </Button>
        </Stack>
        <Typography variant="h6">Active tunnels</Typography>
        {!items.length && <Typography color="textSecondary">No active tunnels for this device.</Typography>}
        {items.map(item => (
          <Box key={item.id} sx={{ borderTop: 1, borderColor: 'divider', py: 2 }}>
            <Typography>
              <strong>
                {item.host}:{item.port}
              </strong>{' '}
              — {item.state}
              {item.upstream_reachable === false ? ' · target unreachable from device' : ''}
            </Typography>
            <Typography variant="body2">
              {item.access === 'public' ? 'Public' : `Restricted to ${item.allow_cidr}`} · Expires {date(item.expires_at)}
            </Typography>
            <Typography variant="body2">
              Last verified {date(item.checked_at)} · Created by {item.creator}
            </Typography>
            {item.state === 'unknown' && (
              <Typography variant="body2">Status unverified. This is the last known endpoint; device-local expiry remains enforced.</Typography>
            )}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              {item.endpoint && (
                <>
                  <Typography component="code">{item.endpoint}</Typography>
                  <Button
                    onClick={() =>
                      perform(async () => {
                        await navigator.clipboard.writeText(item.endpoint!);
                        return { notice: 'Endpoint copied.' };
                      })
                    }
                  >
                    Copy
                  </Button>
                  <Button component="a" href={item.endpoint.replace('tcp:', 'http:')} target="_blank" rel="noopener noreferrer">
                    HTTP
                  </Button>
                  <Button component="a" href={item.endpoint.replace('tcp:', 'https:')} target="_blank" rel="noopener noreferrer">
                    HTTPS
                  </Button>
                </>
              )}
              {!terminal(item) && (
                <Button color="error" disabled={busy} onClick={() => perform(signal => tunnelRequest(device, `/${item.id}`, 'DELETE', undefined, signal))}>
                  Stop tunnel
                </Button>
              )}
            </Stack>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
